import type { LabelSceneVisibility } from '~/composables/useThreeDRepo'

export type ThreeDEditMode = 'read' | 'write'

export interface ModelMaterials {
  id: number
  material_name: string | null
  material_files: MaterialFileData[]
  material: string
  diffuse: string
  lighting_model: 'B' | 'C' | 'P' | 'L' // B=blinn C=constant P=phong L=lambert
  cull_type: 'B' | 'F' | 'N' // B=backside F=frontside N=doubleside
  blend_mode: 'A' | 'D' | 'U' | 'L' // A=alpha D=Add S=Subtract L=multiply
  material_file_id: number | null
  transparency: number // TODO: What's the difference between this and opacity?
}

export interface MaterialFileData {
  platform: 'web' | 'mobile_web' | 'ios'
  file: {
    url: string
    name: string
    size: number
  }
}

export interface NodeFile {
  type: 'gltf_draco' | 'gltf_mobile' | 'scene'
  model3d_id: number
  node_id: number | null
  file: File
  id?: number
  file_id?: number
}

export interface ModelNode {
  id: number
  model3d_id: number
  display_name: string
  node_name: string
  category: string | null
  layer: number
  opacity: number
  billboard: boolean
  is_root: boolean
  is_layer: boolean
  materials: ModelMaterials[]
  parent_id: number | null
  node_files: NodeFile[]
}

export interface Vec2Data {
  x: number
  y: number
}

export interface Vec3Data extends Vec2Data {
  z: number
}

export interface Vec4Data extends Vec3Data {
  w: number
}

export type CameraViewData = {
  fov: Vec2Data
  position: Vec3Data
  orientation?: Vec4Data
  target: Vec3Data
}

export interface CameraNode {
  node_id: number
  is_visible: boolean
}

export type NodeVisibility = { nodeId: number; visible: boolean }
export type LabelVisibility = { labelId: string; visible: boolean }
export type DrillLabelVisibility = { drillLabelId: number; visible: boolean }

export function isNodeVisibility(layer: NodeVisibility[] | LabelVisibility[] | DrillLabelVisibility[]): layer is NodeVisibility[] {
  return (layer[0] as NodeVisibility).nodeId !== undefined
}

export function isLabelVisibility(layer: NodeVisibility[] | LabelVisibility[] | DrillLabelVisibility[]): layer is LabelVisibility[] {
  return (layer[0] as LabelVisibility).labelId !== undefined
}

export function isDrillLabelVisibility(
  layer: NodeVisibility[] | LabelVisibility[] | DrillLabelVisibility[]
): layer is DrillLabelVisibility[] {
  return (layer[0] as DrillLabelVisibility).drillLabelId !== undefined
}

export type HiddenNode = {
  id: number
  camera_id: number
  node_id: number
  node_name: string | null
}

export type CameraData = {
  id: number
  title: string
  camera_order: number
  is_primary: boolean
  camera: CameraViewData
  hidden_nodes: readonly HiddenNode[]
  visible_nodes: readonly number[]
  model3d_id: number
  text_labels: readonly LabelSceneVisibility[]
  preview?: Preview
  updated_at: string
}

export type Preview = {
  camera_id: number
  id: number
  image: string
}

export interface File {
  url: string
  size?: number
}

export interface Model3D {
  id: number
  company_id: number
  title: string | null
  description: string | null
  gltf_mobile_file: File | null
  gltf_draco_ktx2: string | null
  gltf_draco: string | null
  gltf: string | null
  nodes: ModelNode[]
  cameras: CameraData[]
  updated_at: string
  text_scale: number
  version: 1 | 2
}

export interface IncludeCameras {
  cameras: CameraData[]
}

export interface IncludePreview {
  preview: ScenePreview
}

export type CameraWithPreview = CameraData & IncludePreview

export interface ScenePreview {
  id: number
  camera_id: number
  image: string
  size: number
  mimetype: string
  width: number
  height: number
  created_at: string
  updated_at: string
}

export interface LoadingState {
  state: 'LOADING'
  progress: number // percentage in [0, 1]
  totalSize: number | null // in MB if known
}

export interface LoadedState {
  state: 'LOADED'
}

export type ViewerState = LoadingState | LoadedState

export type LayerSceneElement = { nodeId: number; title: string }
export type LabelSceneElement = { labelId: string }
export type HotspotSceneElement = { hotspotId: number }
export type SceneElement = LayerSceneElement | LabelSceneElement | HotspotSceneElement

export interface ApiLabel {
  created_at: string
  flagpole_visible: boolean
  flagpole_index: number
  id: number
  model3d_id: number
  node_id: number
  node_name: string
  offset_x: number
  offset_y: number
  offset_z: number
  position_x: number
  position_y: number
  position_z: number
  scene_visibility: SceneVisibility[]
  text: string
  updated_at: string
  auto_scale: boolean
  style: null | { text_size: number; auto_scale_size: number }
}

interface SceneVisibility {
  camera_id: number
  is_visible: boolean
  opacity: number
  text_label_id: number
}
